import React, { useState, useEffect } from 'react';
import { getAllAnnotations, deleteAnnotation } from '../api';
import { InfoTooltip, LoadingState, ErrorState, EmptyState, ConfirmDialog } from './shared/UIComponents';

const AnnotationManager = () => {
  const [annotations, setAnnotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, accurate, review_needed, incorrect
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  useEffect(() => {
    loadAnnotations();
  }, []);

  const loadAnnotations = async () => {
    try {
      setLoading(true);
      const data = await getAllAnnotations();
      setAnnotations(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (compoundId) => {
    try {
      await deleteAnnotation(compoundId);
      // Reload annotations after deletion
      loadAnnotations();
    } catch (err) {
      setError(err.message);
    } finally {
      setPendingDeleteId(null);
    }
  };

  const getAnnotationDisplay = (type) => {
    const styles = {
      accurate: { bg: 'var(--bg-card)', color: 'var(--primary)', border: '1px solid var(--primary)' },
      review_needed: { bg: 'var(--bg-card)', color: '#d97706', border: '1px solid #d97706' },
      incorrect: { bg: 'var(--bg-card)', color: '#dc2626', border: '1px solid #dc2626' }
    };
    
    const labels = {
      accurate: '✓ Accurate',
      review_needed: '⚠ Review',
      incorrect: '✗ Incorrect'
    };
    
    return { style: styles[type] || {}, label: labels[type] || 'Unknown' };
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const filteredAnnotations = annotations.filter(annotation => {
    // Filter by annotation type
    const matchesFilter = filter === 'all' || annotation.annotation === filter;
    
    // Filter by search term
    const matchesSearch = searchTerm === '' || 
      annotation.compound_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (annotation.notes && annotation.notes.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesFilter && matchesSearch;
  });

  const getStats = () => {
    const total = annotations.length;
    const accurate = annotations.filter(a => a.annotation === 'accurate').length;
    const reviewNeeded = annotations.filter(a => a.annotation === 'review_needed').length;
    const incorrect = annotations.filter(a => a.annotation === 'incorrect').length;

    return { total, accurate, reviewNeeded, incorrect };
  };

  const stats = getStats();

  if (loading) {
    return <LoadingState message="Loading annotations..." />;
  }

  if (error) {
    return <ErrorState message={error} />;
  }

  return (
    <div className="component-card">
      <InfoTooltip 
        title="Annotation Management" 
        content="View, filter, and manage all annotations. Export annotations for analysis or backup."
      >
        <h2>Annotation Management</h2>
      </InfoTooltip>
      
      {/* Statistics */}
      <div style={{ 
        marginBottom: '24px', 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
        gap: '16px' 
      }}>
        <div style={{ 
          padding: '16px', 
          background: 'var(--bg)', 
          border: '1px solid var(--border)', 
          borderRadius: '8px', 
          textAlign: 'center' 
        }}>
          <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--primary)' }}>{stats.total}</div>
          <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Total Annotations</div>
        </div>
        <div style={{ 
          padding: '16px', 
          background: 'var(--bg)', 
          border: '1px solid var(--border)', 
          borderRadius: '8px', 
          textAlign: 'center' 
        }}>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#059669' }}>{stats.accurate}</div>
          <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Accurate</div>
        </div>
        <div style={{ 
          padding: '16px', 
          background: 'var(--bg)', 
          border: '1px solid var(--border)', 
          borderRadius: '8px', 
          textAlign: 'center' 
        }}>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#d97706' }}>{stats.reviewNeeded}</div>
          <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Review Needed</div>
        </div>
        <div style={{ 
          padding: '16px', 
          background: 'var(--bg)', 
          border: '1px solid var(--border)', 
          borderRadius: '8px', 
          textAlign: 'center' 
        }}>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#dc2626' }}>{stats.incorrect}</div>
          <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Incorrect</div>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div style={{ 
        marginBottom: '24px', 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '16px' 
      }}>
        {/* Search Input */}
        <div className="form-group">
          <label className="form-group-label">Search</label>
          <input
            type="text"
            placeholder="Search by compound ID or notes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-control"
          />
        </div>

        {/* Filter Buttons */}
        <div className="form-group">
          <label className="form-group-label">Filter by Type</label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { key: 'all', label: 'All', count: stats.total },
              { key: 'accurate', label: 'Accurate', count: stats.accurate },
              { key: 'review_needed', label: 'Review', count: stats.reviewNeeded },
              { key: 'incorrect', label: 'Incorrect', count: stats.incorrect }
            ].map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  borderRadius: '6px',
                  border: '1px solid var(--border)',
                  background: filter === key ? 'var(--primary)' : 'var(--bg-card)',
                  color: filter === key ? 'var(--white)' : 'var(--text)',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {label} ({count})
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="form-group">
          <label className="form-group-label">Results</label>
          <div style={{ 
            padding: '12px 16px', 
            border: '1px solid var(--border)', 
            borderRadius: '8px', 
            background: 'var(--bg)', 
            color: 'var(--text)', 
            textAlign: 'center', 
            fontWeight: '600' 
          }}>
            {filteredAnnotations.length} of {annotations.length}
          </div>
        </div>
      </div>

      {/* Annotations Table */}
      {filteredAnnotations.length === 0 ? (
        <EmptyState 
          message={filter === 'all' ? 'No annotations found.' : `No ${filter.replace('_', ' ')} annotations found.`}
          icon="📝"
        />
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: 'var(--bg)' }}>
              <tr>
                <th style={{ 
                  padding: '12px 16px', 
                  textAlign: 'left', 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: 'var(--text)', 
                  borderBottom: '1px solid var(--border)' 
                }}>
                  Compound ID
                </th>
                <th style={{ 
                  padding: '12px 16px', 
                  textAlign: 'left', 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: 'var(--text)', 
                  borderBottom: '1px solid var(--border)' 
                }}>
                  Annotation
                </th>
                <th style={{ 
                  padding: '12px 16px', 
                  textAlign: 'left', 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: 'var(--text)', 
                  borderBottom: '1px solid var(--border)' 
                }}>
                  Notes
                </th>
                <th style={{ 
                  padding: '12px 16px', 
                  textAlign: 'left', 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: 'var(--text)', 
                  borderBottom: '1px solid var(--border)' 
                }}>
                  Timestamp
                </th>
                <th style={{ 
                  padding: '12px 16px', 
                  textAlign: 'left', 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: 'var(--text)', 
                  borderBottom: '1px solid var(--border)' 
                }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAnnotations.map((annotation, index) => {
                const display = getAnnotationDisplay(annotation.annotation);
                return (
                  <tr key={annotation.compound_id} style={{ 
                    background: index % 2 === 0 ? 'var(--bg-card)' : 'var(--bg)',
                    borderBottom: '1px solid var(--border)'
                  }}>
                    <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>
                      {annotation.compound_id}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                      <span style={{
                        padding: '4px 8px',
                        fontSize: '12px',
                        borderRadius: '4px',
                        ...display.style
                      }}>
                        {display.label}
                      </span>
                    </td>
                    <td style={{ 
                      padding: '12px 16px', 
                      fontSize: '14px', 
                      color: 'var(--text-muted)',
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }} title={annotation.notes || 'No notes'}>
                      {annotation.notes || (
                        <span style={{ fontStyle: 'italic' }}>No notes</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '14px', color: 'var(--text-muted)' }}>
                      {formatTimestamp(annotation.timestamp)}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                      <button
                        onClick={() => setPendingDeleteId(annotation.compound_id)}
                        style={{
                          padding: '4px 8px',
                          fontSize: '12px',
                          borderRadius: '4px',
                          border: '1px solid var(--border)',
                          background: 'var(--bg-card)',
                          color: '#dc2626',
                          cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Export Options */}
      <div style={{ 
        marginTop: '24px', 
        padding: '16px', 
        background: 'var(--bg)', 
        border: '1px solid var(--border)', 
        borderRadius: '8px' 
      }}>
        <h4 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)', margin: '0 0 12px 0' }}>
          Export Options
        </h4>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => {
              const dataStr = JSON.stringify(filteredAnnotations, null, 2);
              const dataBlob = new Blob([dataStr], { type: 'application/json' });
              const url = URL.createObjectURL(dataBlob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `annotations_${filter}_${new Date().toISOString().split('T')[0]}.json`;
              link.click();
            }}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              borderRadius: '6px',
              border: 'none',
              background: 'var(--primary)',
              color: 'var(--white)',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Export {filter === 'all' ? 'All' : filter.replace('_', ' ')} Annotations (JSON)
          </button>
          <button
            onClick={() => {
              const csvContent = [
                ['Compound ID', 'Annotation', 'Notes', 'Timestamp'],
                ...filteredAnnotations.map(a => [
                  a.compound_id,
                  a.annotation,
                  a.notes || '',
                  a.timestamp || ''
                ])
              ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');
              
              const dataBlob = new Blob([csvContent], { type: 'text/csv' });
              const url = URL.createObjectURL(dataBlob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `annotations_${filter}_${new Date().toISOString().split('T')[0]}.csv`;
              link.click();
            }}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              borderRadius: '6px',
              border: '1px solid var(--border)',
              background: 'var(--bg-card)',
              color: 'var(--text)',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Export as CSV
          </button>
        </div>
      </div>
      <ConfirmDialog
        open={Boolean(pendingDeleteId)}
        title="Delete annotation?"
        message="This annotation will be permanently removed."
        confirmLabel="Delete"
        onConfirm={() => handleDelete(pendingDeleteId)}
        onCancel={() => setPendingDeleteId(null)}
      />
    </div>
  );
};

export default AnnotationManager;

